import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import { AudioPlayer } from "../components/AudioPlayer";
import { ArrowLeft } from "lucide-react";

export default function EpisodeDetail() {
    const { id } = useParams();
    const [ep, setEp] = useState(null);
    const [guests, setGuests] = useState([]);

    useEffect(() => {
        api.get(`/episodes/${id}`).then(async (r) => {
            setEp(r.data);
            if (r.data.guest_ids?.length) {
                const all = await api.get("/guests");
                setGuests((all.data || []).filter((g) => r.data.guest_ids.includes(g.id)));
            }
        });
    }, [id]);

    if (!ep) return <div className="px-6 py-20 font-serif text-2xl">Loading…</div>;

    const resolveAudioSrc = () => {
        if (!ep.audio_url) return null;
        if (ep.audio_url.startsWith("http")) return ep.audio_url;
        return `${BACKEND_URL}${ep.audio_url}`;
    };
    const audioSrc = resolveAudioSrc();

    return (
        <div data-testid="episode-detail" className="px-6 md:px-12 lg:px-20 py-12 md:py-20 max-w-5xl mx-auto">
            <Link to="/episodes" className="inline-flex items-center gap-2 eyebrow text-black/60 hover:text-[#B8860B] mb-10">
                <ArrowLeft size={14} /> All Episodes
            </Link>
            <div className="mb-12">
                <div className="eyebrow text-[#B8860B] mb-5">
                    {ep.episode_number != null && `Episode ${String(ep.episode_number).padStart(2, "0")}`}
                    {ep.duration && ` · ${ep.duration}`}
                </div>
                <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.05] mb-8">{ep.title}</h1>
                <p className="text-lg md:text-xl text-black/70 font-light leading-relaxed max-w-3xl">{ep.description}</p>
            </div>

            {ep.cover_image && (
                <img src={ep.cover_image} alt={ep.title} className="w-full h-[300px] md:h-[480px] object-cover mb-10" />
            )}

            {audioSrc && (
                <div className="mb-8">
                    <AudioPlayer src={audioSrc} title={ep.title} />
                </div>
            )}

            {ep.external_url && (
                <div className="mb-12 flex flex-wrap gap-3">
                    <a
                        href={ep.external_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="listen-spotify-link"
                        className="kind-btn-secondary"
                    >
                        Listen on Spotify ↗
                    </a>
                </div>
            )}

            {ep.embed_url && (
                <div className="mb-12">
                    <div className="eyebrow text-black/60 mb-4">Spotify Player</div>
                    <iframe
                        src={ep.embed_url}
                        width="100%"
                        height="232"
                        frameBorder="0"
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                        title="Spotify Player"
                        data-testid="spotify-embed"
                    />
                </div>
            )}

            {guests.length > 0 && (
                <div className="border-t border-black/10 pt-10">
                    <div className="eyebrow text-[#B8860B] mb-6">Featuring</div>
                    <div className="grid md:grid-cols-2 gap-10">
                        {guests.map((g) => (
                            <div key={g.id} className="flex gap-5">
                                {g.photo_url && <img src={g.photo_url} alt={g.name} className="w-24 h-24 object-cover" />}
                                <div>
                                    <h4 className="font-serif text-2xl tracking-tight">{g.name}</h4>
                                    <div className="eyebrow text-[#B8860B] text-[10px] mt-1 mb-2">{g.title}</div>
                                    <p className="text-sm text-black/60 font-light line-clamp-3">{g.bio}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
